import { useState, useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { SidebarProvider } from "./components/ui/sidebar";
import { useSidebar } from "./components/ui/useSidebar";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";
import { Header } from "./components/Header";
import { AppSidebar } from "./components/AppSidebar";
import { DashboardContent } from "./components/DashboardContent";
import { SetsContent } from "./components/SetsContent";
import { PracticeContent } from "./components/PracticeContent";
import { AIAssistanceContent } from "./components/AIAssistanceContent";
import { MyProfileContent } from "./components/MyProfileContent";
import { ChangeSwimmerContent } from "./components/ChangeSwimmerContent";
import { InboxContent } from "./components/InboxContent";
import { ApplyToCoachContent } from "./components/ApplyToCoachContent";
import { PayoutDetailsContent } from "./components/PayoutDetailsContent";
import { ChangePasswordContent } from "./components/ChangePasswordContent";
import { PaymentMethodsContent } from "./components/PaymentMethodsContent";
import { YourListingsContent } from "./components/YourListingsContent";
import { AIKnowledgeContent } from "./components/AIKnowledgeContent";
import { SettingsContent } from "./components/SettingsContent";
import { CurrentPlanContent } from "./components/CurrentPlanContent";
import { LandingPage } from "./components/LandingPage";
import { BlogPage } from "./components/BlogPage";
import { SignInPage } from "./components/SignInPage";
import { SignUpPage } from "./components/SignUpPage";
import { ForgotPasswordPage } from "./components/ForgotPasswordPage";
import { SetsContentWrapper } from "./components/SetsContentWrapper";
import { ToolsPage } from "./components/ToolsPage";
import { PracticeClockContent } from "./components/PracticeClockContent";
import { SharePage } from "./components/SharePage";
import { ErrorBoundary } from "./components/ui/errorBoundary";
import { NotificationContent } from "./components/NotificationContent";
import { toast } from "sonner";
import { StripeRepository } from "./infrastructure/repositories/StripeRepository";
import { PaymentComplete } from "./components/PaymentComplete";
import { ProSubscriptionProduct } from "./components/ProSubscriptionProduct";
import { BlogDetailPage } from "./components/BlogDetailPage";
import { CreateBlogPage } from "./components/CreateBlogPage";
import { Blog, Pool } from "@/types";
import { FindPoolPage } from "./components/FindPool";
import { CreatePoolPage } from "./components/CreatePoolPage";
import { PoolDetailPage } from "./components/PoolDetailPage";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsOfService } from "./components/TermsOfService";
import { ContactUs } from "./components/ContactUs";
import { secureStorage } from "./utils/secureStorage";
import { isTokenExpired } from "./utils/authToken";

function ContentArea({
  activeSection,
  onSectionChange,
  onLogout,
  clockData,
  showPaymentComplete,
  paymentCanceled,
  onPaymentFinish,
  //blogs,
  pools,
  //setBlogs,
  setPools
}: {
  activeSection: string;
  onSectionChange: (section: string, payload?: any) => void;
  onLogout?: () => void;
  clockData?: { sets?: any[]; practiceId?: number; name?: string};
  showPaymentComplete?: boolean;
  paymentCanceled?: boolean;
  onPaymentFinish?: (goHome?: boolean) => void;
  blogs: Blog[];
  pools: Pool[];
  setBlogs: React.Dispatch<React.SetStateAction<Blog[]>>;
  setPools: React.Dispatch<React.SetStateAction<Pool[]>>;
}) {
  const { state } = useSidebar();
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(() => {
    return localStorage.getItem("pro_subscription_active") === "true" ? true : null;
  });
  const stripeRepo = new StripeRepository();
  const checkSubscription = async () => {
    try {
      const userId =
        localStorage.getItem("current_user_swimmer_id") ||
        localStorage.getItem("selected_swimmer_id") ||
        "";

      if (!userId) {
        setHasSubscription(false);
        localStorage.setItem("pro_subscription_active", "false");
        return;
      }

      const res = await stripeRepo.getCurrentSubscription(String(userId));
      const payload = res?.response ?? res ?? {};

      const status = Number(payload?.status ?? 0);
      const data = payload?.data;
      const msg = String(payload?.msg ?? payload?.message ?? "").toLowerCase();
      const noCurrentPlan =
        msg.includes("no current plan") || msg.includes("no subscription");

      // API can return { status: 1, msg: "No subscription found for this user", data: null }.
      // In that case, treat as not subscribed.
      if (status !== 1 || noCurrentPlan || !data || typeof data !== "object") {
        setHasSubscription(false);
        localStorage.setItem("pro_subscription_active", "false");
        return;
      }

      const subscriptionStatus = String(
        data?.subscription_status ?? data?.status ?? data?.subscription ?? ""
      ).toLowerCase();

      const isActive = !["", "none", "canceled", "cancelled", "inactive", "expired"].includes(
        subscriptionStatus
      );

      setHasSubscription(isActive);
      localStorage.setItem("pro_subscription_active", isActive ? "true" : "false");
    } catch (err) {
      setHasSubscription(false);
      localStorage.setItem("pro_subscription_active", "false");
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [activeSection]);

  const [dismissVersion, setDismissVersion] = useState(0);
  console.log(dismissVersion);

//  const getDismissKey = (section: string) => `pro_dismissed_${section}`;

const PRACTICE_PAYWALL_KEY = "pro_dismissed_practice_create_set";

const isPracticeOrCreateSet = (section: string) =>
  section === "practice" || section === "create-set";

const isDismissed = (section: string) => {
  if (isPracticeOrCreateSet(section)) {
    return localStorage.getItem(PRACTICE_PAYWALL_KEY) === "true";
  }
  return localStorage.getItem(`pro_dismissed_${section}`) === "true";
};

const dismiss = (section: string) => {
  if (isPracticeOrCreateSet(section)) {
    localStorage.setItem(PRACTICE_PAYWALL_KEY, "true");
  } else {
    localStorage.setItem(`pro_dismissed_${section}`, "true");
  }
  setDismissVersion(v => v + 1);
};


  // const isDismissed = (section: string) =>  localStorage.getItem(getDismissKey(section)) === "true";

  // const dismiss = (section: string) => {
  //   localStorage.setItem(getDismissKey(section), "true");
  //   setDismissVersion(v => v + 1);
  // };

  const renderContent = () => {
if (showPaymentComplete) {
  return (
    <PaymentComplete
      canceled={paymentCanceled === true}
      onFinish={(goHome) => onPaymentFinish?.(goHome === "home")}
    />
  );
}

if (hasSubscription === null) {
  return (
    <div className="flex items-center justify-center h-full">
      <div>
        <div className="animate-spin h-10 w-10 rounded-full border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-center text-gray-600">Checking subscription...</p>
      </div>
    </div>
  );
}

const isPracticeOrSets =  activeSection === "practice" || activeSection === "create-set";

const isAiOrShare =  activeSection === "ai-assistance" || activeSection.startsWith("share");
const hasBlogAccess = secureStorage.get<boolean>("has_blog_access") === true;
const hasPoolAccess = secureStorage.get<boolean>("has_pool_access") === true;

if (!hasSubscription) {
   if (isAiOrShare) {
     return <ProSubscriptionProduct />;
   }

   if (isPracticeOrSets && !isDismissed(activeSection)) {
     return (
       <ProSubscriptionProduct
         onClose={() => dismiss(activeSection)}
       />
     );
   }
}

    if (activeSection.startsWith("share")) {
    const tab = activeSection.replace("share-", "") || "feed";
    return (
      <ErrorBoundary>
        <SharePage forcedTab={tab} />
      </ErrorBoundary>
    );  
  }

    switch (activeSection) {
      case "practice":
        return <PracticeContent onSectionChange={onSectionChange} />;
      case "sets":
        return <SetsContentWrapper onSectionChange={onSectionChange} />;
      case "create-set":
        return <SetsContent onSectionChange={onSectionChange} showManageActions />;
      case "practiceclock":
        return (
          <PracticeClockContent
            sets={clockData?.sets || []}
            practiceId={clockData?.practiceId}
            name={clockData?.name}
            onSectionChange={onSectionChange}
          />
        );
        // case "create-blog":
        //   return (
        //     <CreateBlogPage
        //       blogs={blogs}
        //       onBlogsChange={setBlogs}
        //     />
        //   );
    case "create-blog":
      return hasBlogAccess ? <CreateBlogPage /> : <DashboardContent onSectionChange={onSectionChange} />;
    case "add-pool":
      return hasPoolAccess ? (
        <CreatePoolPage
          pools={pools}
          onPoolsChange={setPools}
        />
      ) : (
        <DashboardContent onSectionChange={onSectionChange} />
      );
      case "ai-assistance":
        return <AIAssistanceContent />;
      case "tools":
        return <ToolsPage />;
      case "my-profile":
        return <MyProfileContent />;
      case "change-swimmer":
        return <ChangeSwimmerContent />;
      case "inbox":
        return <InboxContent />;
      case "notification":
        return <NotificationContent />;
      case "apply-to-coach":
        return <ApplyToCoachContent />;
      case "payout-details":
        return <PayoutDetailsContent />;
      case "change-password":
        return <ChangePasswordContent />;
      case "payment-methods":
        return <PaymentMethodsContent />;
      case "current-plan":
        return <CurrentPlanContent />;
      case "your-listings":
        return <YourListingsContent />;
      case "ai-knowledge":
        return <AIKnowledgeContent />;
      case "settings":
        return <SettingsContent />;
      case "home":
      default:
        return <DashboardContent onSectionChange={onSectionChange} />;
    }
  };

  return (
    <div
      className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
        state === "expanded" ? "ml-0 xl:ml-64" : "ml-0"
      }`}
    >
      <Header onSectionChange={onSectionChange} onLogout={onLogout} />
      <main className="flex-1 p-6 min-w-0 overflow-auto">{renderContent()}</main>
    </div>
  );
}

function AppContent() {
  const token = localStorage.getItem("auth_token");
  const hasValidToken = !!token && !isTokenExpired(token);
  const [currentPage, setCurrentPage] = useState("loading");
  const [activeSection, setActiveSection] = useState("home");
  const [clockData, setClockData] = useState<{ sets?: any[]; practiceId?: number; name?: string }>({});
  const { isAuthenticated, logout, isLoading } = useAuth();
  const hasAppAccess = isAuthenticated || hasValidToken;
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [pools, setPools] = useState<Pool[]>([]);

const resolveRoute = (path: string, hasAuth: boolean) => {
  if (path === "/" || path === "/app") {
    return hasAuth
      ? { page: "app", section: "home" }
      : { page: "landing" };
  }

  const appMatch = path.match(/^\/app\/(.+)$/);
  if (appMatch) {
    if (!hasAuth) return { page: "landing" };
    return { page: "app", section: appMatch[1] };
  }

  if (path === "/blog") return { page: "blog" };
  if (path === "/find-pool") return { page: "find-pool" };
  if (path === "/signin") return hasAuth ? { page: "app", section: "home" } : { page: "signin" };
  if (path === "/signup") return hasAuth ? { page: "app", section: "home" } : { page: "signup" };
  if (path === "/forgot-password") return { page: "forgot-password" };
  if (path === "/privacy-policy") return { page: "privacy-policy" };
  if (path === "/contactus") return { page: "contactus" };
  if (path === "/terms-of-service") return { page: "terms-of-service" };

  const blogMatch = path.match(/^\/blog\/(\d+)\/(.+)$/);
  if (blogMatch) return { page: `blog-${blogMatch[1]}-${blogMatch[2]}` };

  const poolMatch = path.match(/^\/pool\/(\d+)/);
  if (poolMatch) return { page: `pool-${poolMatch[1]}` };

  return { page: hasAuth ? "app" : "landing" };
};

useEffect(() => {
  const applyRoute = () => {
    const { page, section } = resolveRoute(
      window.location.pathname,
      hasAppAccess
    );

    setCurrentPage(page);
    if (page === "app") {
      setActiveSection(section ?? "home");
    }
  };

  applyRoute();
  window.addEventListener("popstate", applyRoute);
  return () => window.removeEventListener("popstate", applyRoute);
}, [hasAppAccess]);



  const isPublicRoute = (page: string) => {
    return (
      page === "landing" ||
      page === "blog" ||
      page.startsWith("blog-") ||
      page === "find-pool" ||
      page.startsWith("pool-") ||
      page === "privacy-policy" ||
      page === "terms-of-service" ||
      page === "signin" ||
      page === "signup" ||
      page === "forgot-password"
    );
  };

  // Read payment parameters AFTER state declarations
  const params = new URLSearchParams(window.location.search);
  const paymentStatus = params.get("payment");
  const paymentCanceled = params.get("canceled");
    
  const [hidePaymentOverlay, setHidePaymentOverlay] = useState(false);
  const showPaymentComplete =
          (paymentStatus === "complete" || paymentCanceled === "true") && !hidePaymentOverlay;

  useEffect(() => {
    const handler = (e: any) => {
      const { message, type } = e.detail;
      if (type === "warning") toast.warning(message);
      else toast(message);
    };

    window.addEventListener("global-toast", handler);
    return () => window.removeEventListener("global-toast", handler);
  }, []);

  // useEffect(() => {
  //   const token = localStorage.getItem("auth_token");
  //   if (token) {
  //     setCurrentPage("app");
  //     setHasCheckedAuth(true);
  //   }
  // }, []);

//   useEffect(() => {
//   import("./components/BlogPage").then(mod => {
//     setBlogs(mod.blogs);
//   });
// }, []);

  const handleLogout = () => {
    logout();
    window.history.replaceState({}, "", "/");
    setCurrentPage("landing");
    setActiveSection("home");
    setHasCheckedAuth(false);
  };

  const handleSectionChange = (section: string, payload?: any) => {
    if (section === "practiceclock") {
      setClockData(payload || clockData);
    } else {
      setClockData({});
    }
    setActiveSection(section);
  };

  // useEffect(() => {
  //   if (!isLoading && !hasCheckedAuth) {
  //     setHasCheckedAuth(true);
  //     if (isAuthenticated || hasToken) {
  //       setCurrentPage("app");
  //     } else {
  //       setCurrentPage("landing");
  //     }
  //   }
  // }, [isAuthenticated, isLoading, hasCheckedAuth, hasToken]);

  useEffect(() => {
  if (isLoading || hasCheckedAuth) return;

  setHasCheckedAuth(true);

  if (hasAppAccess) {
    setCurrentPage("app");
    return;
  }

  setCurrentPage(prev =>
    isPublicRoute(prev) ? prev : "landing"
  );
}, [hasAppAccess, isLoading, hasCheckedAuth]);


useEffect(() => {
  if (currentPage !== "app") return;

  const section = activeSection || "home";
  const baseUrl = section === "home"
    ? "/app"
    : `/app/${section}`;

  const currentSearch = window.location.search || "";
  const currentParams = new URLSearchParams(currentSearch);
  const hasCheckoutParams =
    currentParams.get("payment") === "complete" || currentParams.has("session_id");

  const targetUrl = hasCheckoutParams ? `${baseUrl}${currentSearch}` : baseUrl;

  if (`${window.location.pathname}${window.location.search}` !== targetUrl) {
    window.history.replaceState({}, "", targetUrl);
  }
}, [activeSection, currentPage]);

  if (isLoading || currentPage === "loading" || !hasCheckedAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          {/* <p className="mt-4 text-gray-600">Loading...</p> */}
        </div>
      </div>
    );
  }

  if (currentPage.startsWith("blog-")) {
    const [, id, slug] = currentPage.split("-");
    return (
      <BlogDetailPage
        blogId={Number(id)}
        slug={slug}
        onNavigate={setCurrentPage}
      />
    );
  }

  if (currentPage.startsWith("pool-")) {
  const [, id] = currentPage.split("-");
  return (
    <PoolDetailPage
      id={Number(id)}
      onNavigate={setCurrentPage}
    />
  );
}
if (currentPage === "privacy-policy") {
  return <PrivacyPolicy onNavigate={setCurrentPage} />;
}

if (currentPage === "terms-of-service") {
  return <TermsOfService onNavigate={setCurrentPage} />;
}


if (currentPage === "contactus") {
  return <ContactUs onNavigate={setCurrentPage} />;
}

  if (currentPage === "landing")
    return <LandingPage onNavigate={setCurrentPage} />;


if (currentPage === "blog")
  return (
    <BlogPage
      onNavigate={setCurrentPage}
    />
  );


  if (currentPage === "find-pool")
  return (
    <FindPoolPage
      onNavigate={setCurrentPage}
    />
  );

  

  if (currentPage === "signin")
    return <SignInPage onNavigate={setCurrentPage} />;

  if (currentPage === "signup")
    return <SignUpPage onNavigate={setCurrentPage} />;

  if (currentPage === "forgot-password")
    return <ForgotPasswordPage onNavigate={setCurrentPage} />;

  if (currentPage === "app") {
    if (hasAppAccess) {
      return (
        <SidebarProvider>
          <div className="flex min-h-screen w-full">
            <AppSidebar
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
            <ContentArea
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
                onLogout={handleLogout}
                clockData={clockData}
                showPaymentComplete={showPaymentComplete}
                paymentCanceled={paymentCanceled === "true"}
                onPaymentFinish={(goHome) => {
                  window.history.replaceState({}, "", "/");

                  if (goHome) {
                    setActiveSection("home");
                  }

                  setHidePaymentOverlay(true);
                }}
                blogs={blogs}
                pools={pools}
                setBlogs={setBlogs}
                setPools={setPools}
              />
          </div>
        </SidebarProvider>
      );
    } else {
      setCurrentPage("landing");
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Redirecting...</p>
          </div>
        </div>
      );
    }
  }

  return <LandingPage onNavigate={setCurrentPage} />;
}

export default function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

    // Coach requires subscription for entire app
// if (userType === "coach" && hasSubscription === false) {
//   return (
//     <CoachSubscriptionProduct
//       repo={repo}
//       paymentMethodId={null}
//       onSuccess={() => setHasSubscription(true)}
//     />
//   );
// }
